import React, { useState, useCallback, useMemo } from "react";
import { Grid, Card, Typography, Tabs, Tab, Box } from "@mui/material";
import SoftBox from "components/SoftBox";
import axios from "../../api";
import CustomerProfile from "./CustomerProfile";
import ViewAddresses from "./ViewAddresses";
import ViewAssets from "./ViewAssets";
import ViewSubscription from "./ViewSubscriptions";
import ViewCustomPlans from "./ViewCustomPlans";
import ViewDivisions from "./ViewDivisions";
import ViewMembers from "./ViewMembers";
import { CustomerIdContext } from "context/CustomerIdContext";
import SlideOverPanel from "components/SlideOverPanel";
import AddAsset from "layouts/Assets/AddAsset";
import AddSubscription from "layouts/Subscriptions/AddSubscription";
import AddEmployee from "layouts/employees/AddEmployee";

interface Customer {
  name: string;
  type: string;
  email?: string;
  phone?: string;
}

interface CustomerDetailContentProps {
  /** Customer id to load and display (from route or overlay) */
  customerId: string | undefined;
}

/**
 * Reusable customer detail with tabs (Profile, Addresses, Assets, etc.).
 * Used both on the View Customer page and in the Admin Tickets customer overlay.
 * When used in overlay, wrap in CustomerIdContext.Provider so tab content can resolve id.
 */
const CustomerDetailContent = ({ customerId }: CustomerDetailContentProps) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [listNonce, setListNonce] = useState(0);
  const [addAssetOpen, setAddAssetOpen] = useState(false);
  const [addSubscriptionOpen, setAddSubscriptionOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const refreshLists = useCallback(() => {
    setListNonce((n) => n + 1);
  }, []);

  const fetchCustomer = useCallback(async () => {
    if (!customerId) return;
    try {
      const response = await axios.get(`/customer/${customerId}`);
      setCustomer(response?.data || null);
      setTabIndex(0);
    } catch (e) {
      console.error(e);
    }
  }, [customerId]);

  React.useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  const idNum = customerId ? Number(customerId) : NaN;

  const handleOpenAddAsset = useCallback(
    (_cid: number, _ctype: string) => {
      setAddAssetOpen(true);
    },
    []
  );

  const handleOpenAddSubscription = useCallback(
    (_cid: number, _ctype: string) => {
      setAddSubscriptionOpen(true);
    },
    []
  );

  const handleOpenAddMember = useCallback((_cid: number) => {
    setAddMemberOpen(true);
  }, []);

  const closeAddAsset = useCallback(() => {
    setAddAssetOpen(false);
  }, []);

  const closeAddSubscription = useCallback(() => {
    setAddSubscriptionOpen(false);
  }, []);

  const closeAddMember = useCallback(() => {
    setAddMemberOpen(false);
  }, []);

  const assetCustomers = useMemo(() => {
    if (!customerId || Number.isNaN(idNum)) return null;
    return [
      {
        customer_id: idNum,
        name: customer?.name ?? "Customer",
        email: customer?.email ?? "",
        phone: customer?.phone ?? "",
        is_priority: false,
        is_active: true,
        type: customer?.type ?? "customer",
      },
    ];
  }, [customerId, idNum, customer]);

  const tabs = useMemo(() => {
    const t: { label: string; component: React.ReactNode }[] = [
      { label: "Profile", component: <CustomerProfile customer={customer} /> },
    ];
    if (customer?.type === "company") {
      t.push(
        { label: "Divisions", component: <ViewDivisions /> },
        {
          label: "Members",
          component: (
            <ViewMembers
              onOpenAddMember={handleOpenAddMember}
              refreshNonce={listNonce}
            />
          ),
        }
      );
    }
    t.push(
      { label: "Addresses", component: <ViewAddresses /> },
      {
        label: "Assets",
        component: (
          <ViewAssets
            onOpenAddAsset={handleOpenAddAsset}
            customerType={customer?.type ?? "customer"}
            refreshNonce={listNonce}
          />
        ),
      },
      { label: "Custom Plans", component: <ViewCustomPlans /> },
      {
        label: "Subscriptions",
        component: (
          <ViewSubscription
            onOpenAddSubscription={handleOpenAddSubscription}
            customerType={customer?.type ?? "customer"}
            refreshNonce={listNonce}
          />
        ),
      }
    );
    return t;
  }, [
    customer,
    listNonce,
    handleOpenAddAsset,
    handleOpenAddSubscription,
    handleOpenAddMember,
  ]);

  if (!customerId) {
    return null;
  }

  const displayName =
    customer?.name != null && String(customer.name).trim() !== ""
      ? String(customer.name)
      : "Customer";

  return (
    <>
      <CustomerIdContext.Provider value={idNum}>
        <Card
          sx={{
            maxWidth: "100%",
            width: "100%",
            padding: "24px",
            boxShadow: "none",
            minHeight: 0,
            overflow: "visible",
          }}
        >
          <SoftBox mt={2}>
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              marginBottom={2}
            >
              <Grid item>
                <Typography variant="h5" component="div" gutterBottom>
                  {displayName}'s Profile
                </Typography>
              </Grid>
            </Grid>
            <Box className="profile-tabs">
              <Tabs value={tabIndex} onChange={handleTabChange}>
                {tabs.map((t) => (
                  <Tab key={t.label} label={t.label} />
                ))}
              </Tabs>
            </Box>

            <Box mt={2}>{tabs[tabIndex]?.component}</Box>
          </SoftBox>
        </Card>
      </CustomerIdContext.Provider>

      <SlideOverPanel
        open={addAssetOpen}
        onClose={closeAddAsset}
        title="Add Asset"
        width={1000}
      >
        {addAssetOpen && !Number.isNaN(idNum) && assetCustomers && (
          <AddAsset
            isOpen
            onClose={closeAddAsset}
            customers={assetCustomers}
            fetchData={refreshLists}
            initialCustomerId={idNum}
            embedded
          />
        )}
      </SlideOverPanel>

      <SlideOverPanel
        open={addSubscriptionOpen}
        onClose={closeAddSubscription}
        title="Add Subscription"
        width={700}
      >
        {addSubscriptionOpen && !Number.isNaN(idNum) && (
          <AddSubscription
            embedded
            initialCustomerId={idNum}
            onSuccess={() => {
              closeAddSubscription();
              refreshLists();
            }}
            onCancel={closeAddSubscription}
          />
        )}
      </SlideOverPanel>

      <SlideOverPanel
        open={addMemberOpen}
        onClose={closeAddMember}
        title="Add Member"
        width={700}
      >
        {addMemberOpen && !Number.isNaN(idNum) && (
          <AddEmployee
            embedded
            initialCustomerId={idNum}
            onSuccess={() => {
              closeAddMember();
              refreshLists();
            }}
            onCancel={closeAddMember}
          />
        )}
      </SlideOverPanel>
    </>
  );
};

export default CustomerDetailContent;
